import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfoCircle, Refresh } from 'iconsax-reactjs';
import {
  IMPORTANCE_ALPHA,
  IMPORTANCE_LABELS,
  MARKER_ZONES,
  formatMinute,
  importanceMarks,
  type Candle,
  type TimeWindow,
  type Timeframe,
  type WindowOccurrence,
  type WindowStats,
} from '@pipyar/core';
import { api } from '@/lib/api';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { TimeWindowChart } from '@/components/chart/TimeWindowChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { TimeframeButtons } from '@/components/chart/TimeframeButtons';
import type { PointMarker, WindowBand } from '@/components/chart/timeWindowPrimitive';
import { NextPoints, type PointBlock } from './NextPoints';
import { useI18n } from '@/lib/i18n';

interface WindowBlock {
  window: TimeWindow;
  stats: WindowStats;
  occurrences: WindowOccurrence[];
  /** Projected ahead of the last candle. */
  futureOccurrences: { startTime: number; endTime: number }[];
}

interface TimeWindowsResponse {
  symbol: string;
  timeframe: Timeframe;
  digits: number;
  storedGmtOffsetSec: number;
  statsBars: number;
  candles: Candle[];
  windows: WindowBlock[];
  points: PointBlock[];
  /** Bar slots after the last candle, for the chart's whitespace. */
  futureBarTimes: number[];
  /** The API's clock when it answered, so countdowns survive a skewed browser. */
  serverTime: number;
}

export function TimeWindowsPage() {
  const { theme } = useTheme();
  // M5 by default: fine enough to see how price behaves inside a one-hour
  // window, without the noise of M1.
  const [timeframe, setTimeframe] = useState<Timeframe>('M5');
  // Marker ids are database integers now that windows are editable.
  const [hidden, setHidden] = useState<Set<number>>(new Set());

  const { symbol } = useSymbol();
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['time-windows', symbol, timeframe],
    queryFn: () =>
      api.get<TimeWindowsResponse>(
        `/api/market/time-windows?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}`,
      ),
    enabled: Boolean(symbol),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const blocks = query.data?.windows ?? [];
  const pointBlocks = query.data?.points ?? [];
  const digits = query.data?.digits ?? 2;

  const bands = useMemo<WindowBand[]>(() => {
    const out: WindowBand[] = [];
    for (const b of blocks) {
      if (hidden.has(b.window.id)) continue;
      // Opacity carries importance: a very-high window should be obvious
      // against a merely-medium one without reading the sidebar.
      const color = `${b.window.color}${IMPORTANCE_ALPHA[b.window.importance]}`;

      for (const o of b.occurrences) {
        out.push({ from: o.startTime, to: o.endTime, color, label: b.window.name });
      }
      for (const o of b.futureOccurrences) {
        out.push({
          from: o.startTime,
          to: o.endTime,
          color,
          label: b.window.name,
          future: true,
        });
      }
    }
    return out;
  }, [blocks, hidden]);

  /**
   * The newest occurrence still on the chart, used to anchor the opening view.
   *
   * Without an anchor, a zoom tight enough to read individual candles would put
   * the band off-screen for most of the day — the Tokyo window is at 03:00, and
   * ~9 hours of M5 bars does not reach back to it from an afternoon.
   */
  const focus = useMemo(() => {
    let newest: { from: number; to: number } | null = null;
    for (const b of blocks) {
      if (hidden.has(b.window.id)) continue;
      for (const o of b.occurrences) {
        if (newest === null || o.startTime > newest.from) {
          newest = { from: o.startTime, to: o.endTime };
        }
      }
    }
    return newest;
  }, [blocks, hidden]);

  const pointMarkers = useMemo<PointMarker[]>(() => {
    const out: PointMarker[] = [];
    for (const b of pointBlocks) {
      // Points share the hidden set with windows; ids come from separate tables
      // so point ids are negated to keep the two from colliding.
      if (hidden.has(-b.point.id)) continue;
      for (const t of b.times) out.push({ at: t, color: b.point.color, label: b.point.name });
      for (const t of b.futureTimes) {
        out.push({ at: t, color: b.point.color, label: b.point.name, future: true });
      }
    }
    return out;
  }, [pointBlocks, hidden]);

  const toggle = (id: number) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const error = query.error;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('tw.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('tw.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.refresh')}
          className="ms-auto"
          onClick={() => void query.refetch()}
        >
          <Refresh size={18} className={query.isFetching ? 'animate-spin' : undefined} />
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : t('tw.error')}</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_19rem]">
        {/* min-w-0 is load-bearing: a 1fr grid column defaults to
            min-width:auto, so it cannot shrink below its content. Once the
            chart canvas has been sized to the viewport by full screen, the
            column would stay that wide on exit and the page would scroll
            sideways forever. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-1">
            <TimeframeButtons value={timeframe} onChange={setTimeframe} />
          </div>

          <ChartFrame
            label={t('tw.chart')}
            fullscreenToolbar={<TimeframeButtons value={timeframe} onChange={setTimeframe} compact />}
            className="group/frame h-[32rem] overflow-hidden glass rounded-2xl"
          >
            {query.isPending ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : (query.data?.candles.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                No candles for {timeframe} yet.
              </div>
            ) : (
              <TimeWindowChart
                className="h-full w-full"
                candles={query.data!.candles}
                bands={bands}
                points={pointMarkers}
                futureTimes={query.data!.futureBarTimes}
                digits={digits}
                theme={theme}
                resetKey={`${symbol}-${timeframe}`}
                focus={focus}
              />
            )}
          </ChartFrame>

          <p className="text-xs text-muted-foreground">
            {bands.length} window occurrence{bands.length === 1 ? '' : 's'} shaded, including
            the next 24 hours ahead of price (outlined) · opened on the most recent one ·
            statistics computed over {query.data?.statsBars.toLocaleString() ?? 0} {timeframe}{' '}
            bars · times on the broker clock (GMT+3)
          </p>
        </section>

        <aside className="order-1 space-y-3 lg:order-2">
          <NextPoints
            blocks={pointBlocks}
            serverTime={query.data?.serverTime}
            hidden={hidden}
            onToggle={toggle}
          />
        </aside>
      </div>

      {/* Window detail sits under the chart rather than beside it: the cards are
          wide, and a row of them reads as a summary of the day where a tall
          column beside the chart competed with it for attention. */}
      <section className="space-y-2">
        <h2 className="text-sm font-medium">{t('tw.windows')}</h2>
        {blocks.length === 0 && !query.isPending ? (
          <div className="rounded-2xl border border-dashed border-border bg-surface/40 p-6 text-center text-sm text-muted-foreground">
            {t('tw.noWindows')}
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {blocks.map((b) => (
              <WindowCard
                key={b.window.id}
                block={b}
                digits={digits}
                visible={!hidden.has(b.window.id)}
                onToggle={() => toggle(b.window.id)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function WindowCard({
  block,
  digits,
  visible,
  onToggle,
}: {
  block: WindowBlock;
  digits: number;
  visible: boolean;
  onToggle: () => void;
}) {
  const { window: w, stats } = block;
  const upPct = stats.upRate === null ? null : Math.round(stats.upRate * 100);

  const { t } = useI18n();

  return (
    <div className={cn('glass rounded-2xl p-3', !visible && 'opacity-55')}>
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2 text-start">
        <span
          className="size-3 shrink-0 rounded-sm"
          style={{ backgroundColor: w.color }}
          aria-hidden="true"
        />
        <span className="text-sm font-medium">{w.name}</span>
        <span
          className="text-sm font-bold leading-none tracking-tight text-primary"
          title={`Importance: ${IMPORTANCE_LABELS[w.importance]}`}
        >
          {importanceMarks(w.importance)}
        </span>
        <span className="tabular ms-auto text-xs text-muted-foreground">
          {formatMinute(w.startMinute)}–{formatMinute(w.endMinute)}
        </span>
      </button>

      <p className="mt-0.5 text-[10px] text-muted-foreground/80">
        {w.timezone
          ? `${MARKER_ZONES.find((z) => z.id === w.timezone)?.label ?? w.timezone} time`
          : 'Broker clock'}
      </p>
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">{w.description}</p>

      {stats.occurrences === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground/70">
          No occurrences on this timeframe — its bars are longer than the window.
        </p>
      ) : (
        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <Stat label={t('tw.occurrences')} value={stats.occurrences.toLocaleString()} />
          <Stat
            label={t('tw.closedUp')}
            value={upPct === null ? '—' : `${upPct}%`}
            tone={upPct === null ? undefined : upPct >= 50 ? 'bull' : 'bear'}
          />
          <Stat label={t('tw.avgRange')} value={stats.avgRange.toFixed(digits)} />
          <Stat
            label={t('tw.avgMove')}
            value={`${stats.avgChange >= 0 ? '+' : ''}${stats.avgChange.toFixed(digits)}`}
            tone={stats.avgChange >= 0 ? 'bull' : 'bear'}
          />
        </dl>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'bull' | 'bear';
}) {
  return (
    <div>
      <dt className="text-[10px] text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'tabular font-medium',
          tone === 'bull' && 'text-bull',
          tone === 'bear' && 'text-bear',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
